// Email Eval Harness — admin API service

import api from './api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface TruthCase {
  id: string;
  description: string;
  email_subject: string;
  email_from: string;
  expected_category: string;
  expected_customer_no: string | null;
  expected_behavior: string;
  tags: string[];
  notes: string;
  has_line_items: boolean;
}

export interface TruthSetListResponse {
  total: number;
  cases: TruthCase[];
}

// Which harness produced a run. `extraction` = the LLM-judged raw-extraction
// eval (runs in-process against prod); `full_pipeline` = the staging harness
// that drives the whole pipeline through the Merlin extractor and
// deterministically reconciles the persisted draft (kanban 90c7a83f).
export type EvalRunKind = 'extraction' | 'full_pipeline';

// Per-case verdict of a full_pipeline run.
export type FullPipelineVerdict = 'pass' | 'held' | 'fail' | 'no_order';

export interface ReconciliationLineVerdict {
  expected_item_code: string | null;
  expected_quantity: number | null;
  status: 'match' | 'match_held' | 'wrong_silent' | 'wrong_held' | 'missing';
  matched_item_code: string | null;
  detail: string;
}

export interface Reconciliation {
  line_verdicts: ReconciliationLineVerdict[];
  extra_persisted: string[];
  orders: { po_number: string | null; extractor: string | null; line_codes: (string | null)[] }[];
  persisted_lines: { item_code: string; quantity: number | null; needs_review: boolean; needs_review_reason: string | null }[];
  summary: string;
}

export interface CaseResult {
  case_id: string;
  ok: boolean;
  reasoning: string;
  judge_confidence: string;
  actual_category: string | null;
  actual_customer_no: string | null;
  actual_line_items: Record<string, unknown>[];
  latency_ms: number;
  error: string | null;
  // full_pipeline rows only; null on extraction rows.
  verdict?: FullPipelineVerdict | null;
  extractor?: string | null;
  reconciliation?: Reconciliation | null;
}

export interface PipelineVersion {
  git_sha: string;
  prompt_hash: string;
  model_id: string;
  timestamp: string;
}

export interface EvalRun {
  run_id: string;
  status: 'running' | 'completed' | 'failed';
  started_at: string;
  completed_at: string | null;
  dry_run: boolean;
  case_filter: string | null;
  pipeline_version: PipelineVersion | null;
  results: CaseResult[];
  // held / no_order are only meaningful for full_pipeline runs (0 otherwise).
  summary: { total: number; passed: number; failed: number; held?: number; no_order?: number };
  run_kind: EvalRunKind;
  extractor?: string | null;
  trigger_source?: string | null;
  environment?: string | null;
  report_uri?: string | null;
}

export interface RunListResponse {
  total: number;
  runs: EvalRun[];
}

// ---------------------------------------------------------------------------
// Full case payload (CRUD endpoints)
// ---------------------------------------------------------------------------

export interface ExpectedLineItem {
  item_code: string | null;
  quantity: number | null;
  unit_price: number | null;
}

export interface TruthCaseDetail {
  id: string;
  case_id: string;
  description: string;
  email_subject: string;
  email_from: string;
  email_body: string;
  stage1_matched_customer_no: string | null;
  stage1_matched_customer_name: string | null;
  stage1_sender_is_internal: boolean | null;
  expected_behavior: string;
  expected_category: string;
  expected_customer_no: string | null;
  expected_line_items: ExpectedLineItem[];
  expected_ship_to_code: string | null;
  tags: string[];
  notes: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// POST payload — matches EmailTruthCaseCreate.
export interface TruthCaseCreatePayload {
  case_id: string;
  description: string;
  email_subject: string;
  email_from: string;
  email_body: string;
  stage1_matched_customer_no?: string | null;
  stage1_matched_customer_name?: string | null;
  stage1_sender_is_internal?: boolean | null;
  expected_behavior: string;
  expected_category?: string;
  expected_customer_no?: string | null;
  expected_line_items?: ExpectedLineItem[];
  expected_ship_to_code?: string | null;
  tags?: string[];
  notes?: string;
  is_active?: boolean;
}

// PUT payload — every field optional (exclude_unset).
export type TruthCaseUpdatePayload = Partial<Omit<TruthCaseCreatePayload, 'case_id'>>;

// ---------------------------------------------------------------------------
// Quick-Run (ad-hoc) — no persisted case
// ---------------------------------------------------------------------------

export interface QuickRunRequest {
  email_subject: string;
  email_from: string;
  email_body: string;
  stage1_matched_customer_no?: string | null;
  stage1_matched_customer_name?: string | null;
  stage1_sender_is_internal?: boolean | null;
  expected_behavior?: string | null;
  expected_category?: string | null;
  expected_customer_no?: string | null;
}

export interface QuickRunResponse {
  actual_category: string | null;
  actual_customer_no: string | null;
  actual_line_items: Record<string, unknown>[];
  latency_ms: number;
  error: string | null;
  judge_ok: boolean | null;
  judge_reasoning: string | null;
  judge_confidence: string | null;
}

// ---------------------------------------------------------------------------
// API calls
// ---------------------------------------------------------------------------

export const emailEvalService = {
  listCases: async (): Promise<TruthSetListResponse> => {
    const res = await api.get<TruthSetListResponse>('/admin/email-eval/cases');
    return res.data;
  },

  getCase: async (caseId: string): Promise<TruthCaseDetail> => {
    const res = await api.get<TruthCaseDetail>(
      `/admin/email-eval/cases/${encodeURIComponent(caseId)}`,
    );
    return res.data;
  },

  createCase: async (
    payload: TruthCaseCreatePayload,
  ): Promise<TruthCaseDetail> => {
    const res = await api.post<TruthCaseDetail>(
      '/admin/email-eval/cases',
      payload,
    );
    return res.data;
  },

  updateCase: async (
    caseId: string,
    payload: TruthCaseUpdatePayload,
  ): Promise<TruthCaseDetail> => {
    const res = await api.put<TruthCaseDetail>(
      `/admin/email-eval/cases/${encodeURIComponent(caseId)}`,
      payload,
    );
    return res.data;
  },

  deleteCase: async (caseId: string, hard = false): Promise<void> => {
    await api.delete(
      `/admin/email-eval/cases/${encodeURIComponent(caseId)}` +
        (hard ? '?hard=true' : ''),
    );
  },

  quickRun: async (payload: QuickRunRequest): Promise<QuickRunResponse> => {
    const res = await api.post<QuickRunResponse>(
      '/admin/email-eval/cases/quick-run',
      payload,
    );
    return res.data;
  },

  listRuns: async (runKind?: EvalRunKind): Promise<RunListResponse> => {
    const res = await api.get<RunListResponse>('/admin/email-eval/runs', {
      params: runKind ? { run_kind: runKind } : undefined,
    });
    return res.data;
  },

  getRun: async (runId: string): Promise<EvalRun> => {
    const res = await api.get<EvalRun>(`/admin/email-eval/runs/${runId}`);
    return res.data;
  },

  triggerRun: async (caseId?: string, dryRun = false): Promise<EvalRun> => {
    const res = await api.post<EvalRun>('/admin/email-eval/runs', {
      case_id: caseId ?? null,
      dry_run: dryRun,
    });
    return res.data;
  },

  applySpotCheck: async (
    runId: string,
    overrides: Record<string, { ok: boolean; reason: string }>,
  ): Promise<EvalRun> => {
    const res = await api.post<EvalRun>(
      `/admin/email-eval/runs/${runId}/spot-check`,
      { run_id: runId, overrides },
    );
    return res.data;
  },
};
